// ─── Article content building blocks ─────────────────────────────────────────
//
// These primitives keep individual articles readable: each article is a JSX
// tree of these components rather than a wall of inline styles. They also
// guarantee visual consistency across the whole support section.
//
// Usage in an article body:
//
//   import { P, H, OL, UL, Li, Callout, Code } from '../components'
//
//   <>
//     <P>Some intro text.</P>
//     <H>A section heading</H>
//     <OL><Li>Step one</Li><Li>Step two</Li></OL>
//   </>

// ─── Paragraph ───────────────────────────────────────────────────────────────
export function P({ children }) {
  return (
    <p style={{
      margin: '0 0 16px',
      fontSize: '0.98rem',
      lineHeight: 1.7,
      color: 'var(--ink)',
    }}>
      {children}
    </p>
  )
}

// ─── Section heading (h2 in semantic terms; h1 is the article title) ─────
export function H({ children }) {
  return (
    <h2 style={{
      margin: '32px 0 12px',
      fontSize: '1.2rem',
      lineHeight: 1.3,
      color: 'var(--ink)',
      fontWeight: 600,
    }}>
      {children}
    </h2>
  )
}

// ─── Sub-heading (h3) for nested sections ────────────────────────────────────
export function H3({ children }) {
  return (
    <h3 style={{
      margin: '24px 0 8px',
      fontSize: '1.02rem',
      lineHeight: 1.35,
      color: 'var(--ink)',
      fontWeight: 600,
    }}>
      {children}
    </h3>
  )
}

// ─── Ordered list ────────────────────────────────────────────────────────────
export function OL({ children }) {
  return (
    <ol style={{
      margin: '0 0 16px',
      paddingLeft: 24,
      fontSize: '0.98rem',
      lineHeight: 1.7,
      color: 'var(--ink)',
    }}>
      {children}
    </ol>
  )
}

// ─── Unordered list ──────────────────────────────────────────────────────────
export function UL({ children }) {
  return (
    <ul style={{
      margin: '0 0 16px',
      paddingLeft: 22,
      fontSize: '0.98rem',
      lineHeight: 1.7,
      color: 'var(--ink)',
    }}>
      {children}
    </ul>
  )
}

// ─── List item ───────────────────────────────────────────────────────────────
export function Li({ children }) {
  return (
    <li style={{ marginBottom: 6 }}>{children}</li>
  )
}

// ─── Callout (info, tip, warning, etc.) ──────────────────────────────────────
// Variant defaults to 'info'. Used to highlight side-notes without breaking
// the reading flow.
export function Callout({ variant = 'info', title, children }) {
  const palette = {
    info: {
      bg: '#eff6ff', border: '#bfdbfe', titleColor: '#1e40af',
    },
    tip: {
      bg: '#f0fdf4', border: '#bbf7d0', titleColor: '#166534',
    },
    warning: {
      bg: '#fffbeb', border: '#fde68a', titleColor: '#92400e',
    },
  }[variant] || { bg: '#eff6ff', border: '#bfdbfe', titleColor: '#1e40af' }

  return (
    <div style={{
      background: palette.bg,
      border: `1px solid ${palette.border}`,
      borderRadius: 8,
      padding: '14px 18px',
      margin: '20px 0',
      fontSize: '0.92rem',
      lineHeight: 1.6,
    }}>
      {title && (
        <div style={{
          fontWeight: 600,
          color: palette.titleColor,
          marginBottom: 4,
          fontSize: '0.9rem',
        }}>
          {title}
        </div>
      )}
      <div style={{ color: 'var(--ink)' }}>{children}</div>
    </div>
  )
}

// ─── Inline code (for filenames, fields, button labels) ──────────────────────
export function Code({ children }) {
  return (
    <code style={{
      background: 'var(--surface-2)',
      padding: '1px 6px',
      borderRadius: 4,
      fontSize: '0.88em',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
      color: 'var(--ink)',
    }}>
      {children}
    </code>
  )
}

// ─── Placeholder for "Coming soon" draft articles ────────────────────────────
export function DraftPlaceholder({ title }) {
  return (
    <div style={{
      padding: '32px 24px',
      textAlign: 'center',
      background: 'var(--surface)',
      borderRadius: 8,
      border: '1px dashed var(--surface-3)',
      color: 'var(--ink-muted)',
    }}>
      <p style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 500, color: 'var(--ink)' }}>
        This guide is being written.
      </p>
      <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.55 }}>
        We're still drafting the content for "{title}".<br/>
        Need help with this right now? Just ask us on the contact form below.
      </p>
    </div>
  )
}

// ─── Screenshot ──────────────────────────────────────────────────────────────
//
// Inline image with optional caption, sized to fit inside the article column.
// Has two display modes:
//
//   PLACEHOLDER: when `src` is not supplied, renders a labelled dashed box
//   describing what the screenshot should show. Useful while writing the
//   article before the real image is available.
//
//   IMAGE: when `src` is supplied, renders the actual image with its
//   caption underneath.
//
// Props:
//   src      Image URL. If omitted, placeholder mode kicks in.
//            Convention: drop the file in /public/support-screenshots/
//            and pass src="/support-screenshots/your-file.png".
//   alt      Required when `src` is set. Plain-text description for
//            screen readers and for when the image fails to load.
//   caption  Optional one-line note shown below the image. Also doubles
//            as the placeholder label.
//   width    Optional max-width in px. Defaults to filling the column.
//
// Usage:
//   <Screenshot
//     caption="The 'Send new request' button on the client detail page"
//   />
//
//   …becomes, once the image is captured:
//
//   <Screenshot
//     src="/support-screenshots/send-new-request-button.png"
//     alt="Client detail page with 'Send new request' button highlighted"
//     caption="The 'Send new request' button on the client detail page"
//   />

export function Screenshot({ src, alt, caption, width }) {
  const maxW = width ? `${width}px` : '100%'

  if (!src) {
    return (
      <figure style={{ margin: '20px 0', maxWidth: maxW }}>
        <div
          role="img"
          aria-label={caption ? `Placeholder for screenshot: ${caption}` : 'Screenshot placeholder'}
          style={{
            border: '2px dashed var(--surface-3)',
            background: 'var(--surface)',
            borderRadius: 8,
            padding: '36px 24px',
            textAlign: 'center',
            color: 'var(--ink-muted)',
          }}
        >
          <div style={{
            fontSize: '0.72rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: 'var(--accent)',
            marginBottom: 6,
          }}>
            Screenshot
          </div>
          <div style={{
            fontSize: '0.92rem',
            lineHeight: 1.5,
            color: 'var(--ink)',
            fontStyle: 'italic',
          }}>
            {caption || 'Image to be added.'}
          </div>
        </div>
      </figure>
    )
  }

  return (
    <figure style={{ margin: '20px 0', maxWidth: maxW }}>
      <img
        src={src}
        alt={alt || caption || ''}
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          borderRadius: 8,
          border: '1px solid var(--surface-2)',
        }}
        loading="lazy"
      />
      {caption && (
        <figcaption style={{
          marginTop: 8,
          fontSize: '0.84rem',
          color: 'var(--ink-muted)',
          lineHeight: 1.5,
          textAlign: 'center',
          fontStyle: 'italic',
        }}>
          {caption}
        </figcaption>
      )}
    </figure>
  )
}
